import React, { useState, useRef, useEffect } from 'react';
import { Ticket, Menu, X } from 'lucide-react';
import '../css/header.css';
import { fetchData } from '../helpers/api';

const Header = () => {
  const [settings, setSettings] = useState({});
  const [specialUrl, setspecialUrl] = useState({});
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  useEffect(() => {
    fetchHeaderData();

    // Update current path if it changes
    const handlePathChange = () => {
      setCurrentPath(window.location.pathname);
    };

    window.addEventListener('popstate', handlePathChange);
    return () => {
      window.removeEventListener('popstate', handlePathChange);
    };
  }, []);

  const fetchHeaderData = async () => {
    try {
      const settingsData = await fetchData('/api/setting');
      setSettings(settingsData);

      // Load special menu from local page data
      const specialResponse = await fetch('/api/pages.php?page=menu-special');
      if (specialResponse.ok) {
        const specialData = await specialResponse.json();
        setspecialUrl({
          active: specialData.active || false,
          name: specialData.menuName || '',
          url: '/exposition-speciale',
        });
      }
    } catch (e) {
      console.error('Failed fetching data: ', e);
    }
  };

  // Handle clicks outside the dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Custom dropdown toggle
  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Function to check if a link is active
  const isLinkActive = (path) => {
    // Exact match or match with trailing slash
    return currentPath === path || currentPath === path + '/';
  };

  return (
    <nav className="header bg-white fixed-top shadow-sm">
      <div className="container">
        <div className="d-flex justify-content-between align-items-center py-3">
          <div className="d-flex align-items-center">
            <a href="/" className="d-flex align-items-center">
              <img
                src="/LOGO_MACTe_Web.png"
                alt="Mémorial ACTe"
                style={{ height: '48px', width: 'auto' }}
              />
            </a>
          </div>

          {/* Desktop Navigation */}
          <div className="d-none d-xl-flex align-items-center">
            <a
              href="/visiter-le-macte"
              className={`nav-pill px-3 py-2 rounded-pill mx-1 text-decoration-none fw-bold ${isLinkActive('/visiter-le-macte') ? 'bg-warning  fw-bold text-white' : 'text-dark'
                }`}
            >
              VISITER
            </a>

            {/* Custom dropdown implementation */}
            <div className="mx-1 position-relative" ref={dropdownRef}>
              <span
                className={`nav-pill px-3 py-2 rounded-pill mx-1 text-decoration-none fw-bold ${isLinkActive('/decouvrir-le-macte') ? 'bg-warning  fw-bold text-white' : 'text-dark'
                  }`}
                // className="px-3 py-2 text-dark text-decoration-none d-flex align-items-center cursor-pointer"
                onClick={toggleDropdown}
                style={{ cursor: 'pointer' }}
              >
                DÉCOUVRIR
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="ms-1" viewBox="0 0 16 16">
                  <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                </svg>
              </span>
              {isDropdownOpen && (
                <div className="position-absolute bg-white shadow rounded py-2 mt-1" style={{ zIndex: 1000, minWidth: '250px' }}>
                  <a href="/decouvrir-le-macte" className="d-block px-3 py-2 text-decoration-none text-dark hover-bg-light">Histoire & Architecture</a>
                  <span className="d-block px-3 py-1 text-muted small fw-bold mt-2">Projet d'orientation</span>
                  <a href="/projet-racines-en-mouvement" className="d-block px-3 py-2 ps-4 text-decoration-none text-dark hover-bg-light">Racines en mouvement</a>
                  <a href="/projet-memorial-vision" className="d-block px-3 py-2 ps-4 text-decoration-none text-dark hover-bg-light">Un souffle, un monde</a>
                  <a href="/locations-espaces" className="d-block px-3 py-2 text-decoration-none text-dark hover-bg-light">Locations d'espaces</a>
                </div>
              )}
            </div>

            <a href="/collections"
              className={`nav-pill px-3 py-2 rounded-pill mx-1 text-decoration-none fw-bold ${isLinkActive('/collections') ? 'bg-warning  fw-bold text-white' : 'text-dark'
                }`}
            >COLLECTIONS</a>
            <a href="/evenements"
              className={`nav-pill px-3 py-2 rounded-pill mx-1 text-decoration-none fw-bold ${isLinkActive('/evenements') ? 'bg-warning  fw-bold text-white' : 'text-dark'
                }`}
            >EN CE MOMENT</a>
            <a href="/restaurant-ichali"
              className={`nav-pill px-3 py-2 rounded-pill mx-1 text-decoration-none fw-bold ${isLinkActive('/restaurant-ichali') ? 'bg-warning  fw-bold text-white' : 'text-dark'
                }`}
            >RESTAURANT</a>
            {specialUrl?.active && (
              <a href={specialUrl.url}
                className={`nav-pill px-3 py-2 rounded-pill mx-1 text-decoration-none fw-bold 
                  ${isLinkActive(specialUrl.url) ? 'bg-warning  fw-bold text-white' : 'text-primary'
                  }`}
              >{specialUrl.name}</a>
            )}
          </div>

          <div className="d-flex align-items-center">
            <a href={settings?.defaultBookingUrl || `#`} target="_blank" className="nav-booking bg-dark text-white px-3 py-2 rounded-pill fw-medium me-3 text-decoration-none d-flex align-items-center">
              <Ticket size={18} className="me-2" />
              Billetterie
            </a>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="d-xl-none btn btn-link text-dark p-1 border-0"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Modern Side-sliding Mobile Navigation Menu */}
      <div
        className={`position-fixed top-0 end-0 h-100 bg-white shadow-lg d-xl-none transition-transform ${isMenuOpen ? 'show-menu' : 'hide-menu'}`}
        style={{
          width: '280px',
          zIndex: 1050,
          transform: isMenuOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s ease-in-out'
        }}
      >
        <div className="d-flex flex-column h-100">
          <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
            <h5 className="m-0">Menu</h5>
            <button
              onClick={() => setIsMenuOpen(false)}
              className="btn btn-link text-dark p-1 border-0"
              aria-label="Close menu"
            >
              <X size={24} />
            </button>
          </div>

          <div className="p-3 flex-grow-1 overflow-auto">

            <a
              href="/visiter-le-macte"
              className={`d-block p-2 ps-2 text-decoration-none text-dark fw-medium rounded hover-bg-light mb-2 ${isLinkActive('/visiter-le-macte') ? 'fw-bold text-decoration-underline' : ''
                }`}
            >
              VISITER
            </a>

            <div className="mb-2">
              <div className="fw-medium mb-2 ps-2">DÉCOUVRIR</div>
              <a
                href="/decouvrir-le-macte"
                className={`d-block p-2 ps-4 text-decoration-none text-dark rounded hover-bg-light ${isLinkActive('/decouvrir-le-macte') ? 'fw-bold text-decoration-underline' : ''
                  }`}
              >
                Histoire & Architecture
              </a>
              <span className="d-block p-2 ps-4 text-muted small fw-bold">Projet d'orientation</span>
              <a
                href="/projet-racines-en-mouvement"
                className={`d-block p-2 ps-5 text-decoration-none text-dark rounded hover-bg-light ${isLinkActive('/projet-racines-en-mouvement') ? 'fw-bold text-decoration-underline' : ''
                  }`}
              >
                Racines en mouvement
              </a>
              <a
                href="/projet-memorial-vision"
                className={`d-block p-2 ps-5 text-decoration-none text-dark rounded hover-bg-light ${isLinkActive('/projet-memorial-vision') ? 'fw-bold text-decoration-underline' : ''
                  }`}
              >
                Un souffle, un monde
              </a>
              <a
                href="/locations-espaces"
                className={`d-block p-2 ps-4 text-decoration-none text-dark rounded hover-bg-light ${isLinkActive('/locations-espaces') ? 'fw-bold text-decoration-underline' : ''
                  }`}
              >
                Locations d'espaces
              </a>
            </div>

            <a
              href="/collections"
              className={`d-block p-2 ps-2 text-decoration-none text-dark fw-medium rounded hover-bg-light mb-2 ${isLinkActive('/collections') ? 'fw-bold text-decoration-underline' : ''
                }`}
            >
              COLLECTIONS
            </a>

            <a
              href="/evenements"
              className={`d-block p-2 ps-2 text-decoration-none text-dark fw-medium rounded hover-bg-light mb-2 ${isLinkActive('/evenements') ? 'fw-bold' : ''
                }`}
            >
              EN CE MOMENT
            </a>

            <a
              href="/restaurant-ichali"
              className={`d-block p-2 ps-2 text-decoration-none text-dark fw-medium rounded hover-bg-light mb-2 ${isLinkActive('/restaurant-ichali') ? 'fw-bold' : ''
                }`}
            >
              RESTAURANT ICHALI
            </a>

            {specialUrl?.active && (
              <a
                href={specialUrl.url}
                className={`d-block p-2 ps-2 text-decoration-none text-primary fw-medium rounded hover-bg-light mb-2 
                  ${isLinkActive(specialUrl.url) ? 'fw-bold' : ''
                  }`}
              >
                {specialUrl.name}
              </a>)}
          </div>

          <div className="p-3 border-top">
            <a href={settings?.defaultBookingUrl || `#`} target="_blank" className="d-block bg-dark text-white p-3 rounded-pill fw-medium text-decoration-none text-center">
              <Ticket size={18} className="me-2" />
              Billetterie
            </a>
          </div>
        </div>
      </div>

      {/* Overlay when mobile menu is open */}
      {isMenuOpen && (
        <div
          className="position-fixed top-0 start-0 h-100 w-100 bg-dark d-xl-none"
          style={{
            opacity: '0.5',
            zIndex: 1040
          }}
          onClick={() => setIsMenuOpen(false)}
        />
      )}
    </nav>
  );
};

export default Header;